/**
 * One IPFS object per application:
 *   public  — country / role / DID (safe to list on the map and officer picker)
 *   sealed  — AES-GCM ciphertext for reviewers only
 * On-chain uri = ipfs://cid. contentHash is keccak of the sealed plaintext.
 */

import type { SealedApplication } from './applicantCrypto';
import { sealedLooksCiphertextOnly } from './applicantCrypto';
import type { AppRoleName } from './registryClient';
import type { SealedInsert } from './sealApplication';

export const APPLICATION_ENVELOPE_V = 1 as const;

export interface ApplicationPublicMeta {
  country: string;
  officerDid?: number | null;
  replacesApplicationId?: number | null;
}

export interface ApplicationEnvelopePublic extends ApplicationPublicMeta {
  role: AppRoleName;
  didId: number;
}

export interface ApplicationEnvelope {
  v: typeof APPLICATION_ENVELOPE_V;
  public: ApplicationEnvelopePublic;
  sealed: SealedApplication;
  contentHash: string;
}

const ALLOWED_TOP = new Set(['v', 'public', 'sealed', 'contentHash']);
const ALLOWED_PUBLIC = new Set([
  'role',
  'didId',
  'country',
  'officerDid',
  'replacesApplicationId',
]);

export function parseReplacesId(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function parseOfficerDid(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'Needs Review') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function buildApplicationEnvelope(
  sealed: SealedInsert,
  publicMeta: ApplicationPublicMeta
): ApplicationEnvelope {
  const country = String(publicMeta.country || '').trim();
  if (!country) throw new Error('Application public metadata requires a country.');
  const officerDid = parseOfficerDid(publicMeta.officerDid ?? null);
  const replaces = parseReplacesId(publicMeta.replacesApplicationId);
  return {
    v: APPLICATION_ENVELOPE_V,
    public: {
      role: sealed.role as AppRoleName,
      didId: sealed.did_id,
      country,
      ...(officerDid != null ? { officerDid } : {}),
      ...(replaces != null ? { replacesApplicationId: replaces } : {}),
    },
    sealed: sealed.encrypted_payload,
    contentHash: sealed.content_hash,
  };
}

export function parseApplicationEnvelope(raw: unknown): ApplicationEnvelope {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid application envelope');
  const obj = raw as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (!ALLOWED_TOP.has(key)) {
      throw new Error(`Unexpected envelope key: ${key}`);
    }
  }
  if (obj.v !== APPLICATION_ENVELOPE_V) {
    throw new Error(`Unsupported application envelope version: ${String(obj.v)}`);
  }
  const pub = obj.public as Record<string, unknown> | undefined;
  if (!pub || typeof pub !== 'object') throw new Error('Envelope missing public metadata');
  for (const key of Object.keys(pub)) {
    if (!ALLOWED_PUBLIC.has(key)) {
      throw new Error(`Unexpected public key: ${key}`);
    }
  }
  const role = pub.role;
  if (
    role !== 'athlete' &&
    role !== 'agent' &&
    role !== 'officer' &&
    role !== 'representative'
  ) {
    throw new Error('Envelope public.role is invalid');
  }
  const didId = Number(pub.didId);
  if (!Number.isFinite(didId) || didId <= 0) throw new Error('Envelope public.didId is invalid');
  const country = String(pub.country || '').trim();
  if (!country) throw new Error('Envelope public.country is required');
  const sealed = obj.sealed as SealedApplication;
  if (!sealed || !sealedLooksCiphertextOnly(sealed)) {
    throw new Error('Envelope sealed payload is missing or not ciphertext-only');
  }
  const contentHash = String(obj.contentHash || '');
  if (!contentHash.startsWith('0x') || contentHash.length !== 66) {
    throw new Error('Envelope contentHash is invalid');
  }
  if (sealed.plaintextHash !== contentHash) {
    throw new Error('Envelope contentHash does not match sealed plaintextHash');
  }
  const officerDid = parseOfficerDid(pub.officerDid as number | undefined);
  const replaces = parseReplacesId(pub.replacesApplicationId as number | undefined);
  return {
    v: APPLICATION_ENVELOPE_V,
    public: {
      role,
      didId,
      country,
      ...(officerDid != null ? { officerDid } : {}),
      ...(replaces != null ? { replacesApplicationId: replaces } : {}),
    },
    sealed,
    contentHash,
  };
}
